import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import GlobalHeader from "components/v2/global-header/GlobalHeader";
import { useNativeAppPromoVisibility } from "hooks/useNativeAppPromoVisibility";
import SharedNavigation, { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import { Spinner } from "components/v2/spinner/spinner";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useDispatch, useSelector } from "react-redux";
import {
  getActiveTab,
  getProfileSettings,
  setProfileActiveTab,
  getDealId,
  getEmailAddress,
  getEncompassLoanId,
  getLoanId,
  getLoanNumber,
  getProspectId,
} from "features/authSlice";
import "./shared-layout.scss";
import GlobalOuterLayout from "../global-outer-layout/GlobalOuterLayout";
import SideBar from "components/v2/sidebar/SideBar";
import { PalNotificationHandler } from "components/v2/notifications";
import { NotificationProvider, useNotificationContext } from "contexts/NotificationContext";
import useTabletChecker from "hooks/useTabletChecker";
import Footer from "components/footer/Footer";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { resetScrollPosition } from "utilities/resetScrollPosition";
import {
  DASHBOARD_SESSION_ID,
  DASHBOARD_SESSION_START_TIMESTAMP,
} from "utilities/Constant";
import { trackFSDashboardPageNavigated } from "utilities/full-story";
import { getLoanOfficerPrimaryDomain } from "services/common/loanOfficerSlice";
import type { DashboardShellData } from "../dashboard-shell-content/types/dashboardShellData";
import DashboardTabContent from "./DashboardTabContent";

export interface SharedLayoutProps {
  readonly children?: React.ReactNode;
  propsActiveTab?: string;
  dashboardData: DashboardShellData;
}

const SharedLayoutContent: React.FC<SharedLayoutProps> = ({ propsActiveTab, dashboardData }: SharedLayoutProps) => {
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const { isBannerVisible } = useNativeAppPromoVisibility();
  const dispatch = useDispatch();

  const reduxActiveTab = useSelector(getActiveTab);
  const showProfile = useSelector(getProfileSettings);
  const email = useSelector(getEmailAddress);
  const prospectId = useSelector(getProspectId);
  const loanNumber = useSelector(getLoanNumber);
  const encompassLoanId = useSelector(getEncompassLoanId);
  const loanId = useSelector(getLoanId);
  const dealId = useSelector(getDealId);
  const loPrimaryDomain = useSelector(getLoanOfficerPrimaryDomain);

  useLayoutEffect(() => {
    resetScrollPosition();
  }, [reduxActiveTab, showProfile]);

  const [isNavOpen, setIsNavOpen] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [spinnerDelay, setSpinnerDelay] = useState<NodeJS.Timeout | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [pendingDocumentId, setPendingDocumentId] = useState<string | null>(null);

  const hasBootstrapped = useRef(false);
  const prevShowProfile = useRef(false);

  const { notifications, removeNotification } = useNotificationContext();
  const { data: featureFlag } = useGetFeatureFlagsQuery();

  useEffect(() => {
    globalThis.history.pushState(null, "", globalThis.location.href);

    const handlePopState = () => {
      globalThis.history.pushState(null, "", globalThis.location.href);
    };

    globalThis.addEventListener("popstate", handlePopState);
    return () => globalThis.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (!hasBootstrapped.current && propsActiveTab && propsActiveTab !== reduxActiveTab) {
      dispatch(setProfileActiveTab(propsActiveTab));
    }
    hasBootstrapped.current = true;
  }, [dispatch, propsActiveTab, reduxActiveTab]);

  useEffect(() => {
    if (showProfile && !prevShowProfile.current) {
      trackFSDashboardPageNavigated(getNavTrackingProps("Profile"));
    }
    prevShowProfile.current = showProfile;
  }, [showProfile]);

  const hideSpinner = () => {
    clearTimeout(spinnerDelay as NodeJS.Timeout);
    setShowSpinner(false);
  };

  const triggerSpinner = () => {
    const timeout = setTimeout(() => {
      setShowSpinner(true);
    }, 1000);
    setSpinnerDelay(timeout);
  };

  const getLoSalesDomain = (): string => {
    const domainName = loPrimaryDomain?.domainName?.toLowerCase();
    if (domainName === "retail") return "Retail";
    if (domainName === "cd") return "CD";
    return "Unassigned";
  };

  const getNavTrackingProps = (pageName: string) => ({
    email,
    prospectId,
    sessionId: sessionStorage.getItem(DASHBOARD_SESSION_ID),
    sessionStartTimestamp: sessionStorage.getItem(DASHBOARD_SESSION_START_TIMESTAMP),
    pageName,
    loanNumber: loanNumber || null,
    loanGuid: encompassLoanId || loanId || null,
    dealId: dealId || null,
    loSalesDomain: getLoSalesDomain(),
  });

  const PAGE_NAMES: Record<string, string> = {
    [NavigationIds.Home]: "Dashboard Home",
    [NavigationIds.Tasks]: "Tasks",
    [NavigationIds.Docs]: "Documents",
    [NavigationIds.LoanDetails]: "Loan Details",
  };

  const handleTabChange = (tab: string) => {
    if (tab !== NavigationIds.More) {
      triggerSpinner();
    }
    if (tab !== NavigationIds.More && PAGE_NAMES[tab]) {
      trackFSDashboardPageNavigated(getNavTrackingProps(PAGE_NAMES[tab]));
    }
    dispatch(setProfileActiveTab(tab));
  };

  const handleMobileNotificationClick = async (notificationId: string) => {
    await removeNotification(notificationId);
    setShowNotifications(false);
  };

  const handleDocumentNotificationClick = (palInstanceId: string) => {
    if (palInstanceId) {
      setPendingDocumentId(palInstanceId);
      dispatch(setProfileActiveTab(NavigationIds.Docs));
    }
  };

  const tabContent = (
    <DashboardTabContent
      activeTab={reduxActiveTab}
      showProfile={showProfile}
      dashboardData={dashboardData}
      featureFlags={featureFlag}
      pendingDocumentId={pendingDocumentId}
      onLoad={hideSpinner}
      onDocumentOpened={() => setPendingDocumentId(null)}
    />
  );

  const hideFooter = reduxActiveTab === NavigationIds.More;
  const hideNotifications = reduxActiveTab === NavigationIds.More;

  const renderNavigation = () => (
    <SharedNavigation
      activeTab={reduxActiveTab}
      onTabChange={handleTabChange}
      isDesktop={isDesktop}
      isTablet={isTablet}
      isNavOpen={isNavOpen}
      setIsNavOpen={setIsNavOpen}
    />
  );

  return (
    <GlobalOuterLayout>
      <div className="shared-spinner">{showSpinner && <Spinner isApi />}</div>
      {isDesktop && (
        <>
          <SideBar>{renderNavigation()}</SideBar>
          <div className="global-outer-layout__wrapper">
            <GlobalHeader onDocumentNotificationClick={handleDocumentNotificationClick} hideNotifications={hideNotifications} />
            <main className="main-content">
              {tabContent}
              <Footer />
            </main>
          </div>
        </>
      )}
      {!isDesktop && (
        <div className="mobile-container">
          {!showProfile && (
            <GlobalHeader
              onNotificationsClick={() => setShowNotifications(!showNotifications)}
              onDocumentNotificationClick={handleDocumentNotificationClick}
              hideNotifications={hideNotifications}
              isNavOpen={isNavOpen}
              setIsNavOpen={setIsNavOpen}
            />
          )}
          <div className={`mobile-content ${isBannerVisible ? "mobile-content--banner-visible" : ""}`}>
            {showNotifications ? (
              <PalNotificationHandler
                notifications={notifications}
                onNotificationClick={handleMobileNotificationClick}
                onDocumentNotificationClick={handleDocumentNotificationClick}
                isOpen={showNotifications}
                onClose={() => setShowNotifications(false)}
                triggerElementSelector=".global-header__notification-button"
              />
            ) : (
              <>
                {tabContent}
                {!hideFooter && <Footer />}
              </>
            )}
          </div>
          {renderNavigation()}
        </div>
      )}
    </GlobalOuterLayout>
  );
};

export const SharedLayout: React.FC<SharedLayoutProps> = (props: SharedLayoutProps) => {
  return (
    <NotificationProvider>
      <SharedLayoutContent {...props} />
    </NotificationProvider>
  );
};

export default SharedLayout;
